import type { Idioma } from '../../i18n/index.js';
import { VERSION_TERMINOS } from '@athena/shared';
import type { Seccion } from './comoFunciona.js';

export { VERSION_TERMINOS };

/**
 * Los textos legales y la página de quién está detrás.
 *
 * Los datos son los que Iván fijó el 2026-09-20 (`plan/F4-web.md`): responsable Iván García Villar,
 * autónomo en Reino Unido; marca Sinapser AI, registrada en España; contacto athena@sinapser.com.
 * Normativa UK GDPR, y RGPD para quien visite desde la UE.
 *
 * **Sinapser AI es una marca, no una sociedad**, así que el titular del aviso legal es Iván como
 * persona física. Poner una sociedad que no existe sería justo el tipo de cosa que Athena no hace.
 */
export const CONTACTO = 'athena@sinapser.com';

/** El contacto público de Athena, incluido el de propuestas de estudios. */
export const CONTACTO_SINAPSER = CONTACTO;

/**
 * Quién está detrás: Sinapser y sus productos, contados con los datos de sinapser.com (home y
 * /sobre, a 2026-09-28). Las obligaciones de identidad del titular viven en el aviso legal.
 */
export const QUIEN_ESTA_DETRAS: Readonly<Record<Idioma, readonly Seccion[]>> = {
  es: [
    {
      titulo: 'Por qué hacemos Athena',
      parrafos: [
        'Athena es un producto de Sinapser. Creamos productos y soluciones de inteligencia artificial para la industria de los eventos. Llevamos más de una década construyendo tecnología para el sector.',
        'Con Athena queremos poner el conocimiento de sus estudios al alcance de quienes trabajan en él: ayudarles a entender cómo evoluciona la industria y encontrar datos para sus propuestas, informes y decisiones.',
      ],
    },
    {
      titulo: 'Quién está detrás de Sinapser',
      parrafos: [
        'Sinapser la fundó y la dirige Iván García Villar, cofundador de Eventscase, donde lidera la tecnología desde 2016. La plataforma de eventos creció de 0 a 80 personas y levantó 8 millones de euros hasta su venta en 2024.',
        'Su experiencia aplicando IA a los eventos comenzó en 2013, en Pickevent, la startup londinense de la que nació Eventscase. Allí ya utilizaba IA para hacer recomendaciones.',
      ],
    },
    {
      titulo: 'Otros productos que hemos creado',
      parrafos: [
        'EVA atiende a los asistentes por WhatsApp y web, sin descargar una app: entradas, agenda, avisos y respuestas durante el evento. Con EVA Insights, el organizador puede consultar las preguntas que recibe el asistente.',
        'EVA es el primer agente de IA para eventos por WhatsApp de España y opera eventos reales desde 2024. Fue Innovation Champion en los MPI Iberian Awards 2025.',
        'EVA Brain ayuda a los equipos de eventos a encontrar información en sus correos, reuniones y documentos, con referencias a las fuentes.',
        'También diseñamos activaciones con IA para eventos y ofrecemos formación, consultoría e implantación de IA.',
      ],
    },
    {
      titulo: 'Hablemos',
      parrafos: [
        `¿Conoces un estudio que debería estar en Athena? Escríbenos a ${CONTACTO_SINAPSER}. También puedes contactarnos para hablar de Athena o contarnos un proyecto. Más sobre Sinapser en sinapser.com.`,
      ],
    },
  ],
  en: [
    {
      titulo: 'Why we are building Athena',
      parrafos: [
        'Athena is a Sinapser product. Sinapser creates artificial intelligence products and solutions for the events industry. We have spent over a decade building technology for the sector.',
        'With Athena, we want to make the knowledge in industry studies accessible to the people who work in events: helping them understand how the industry is evolving and find data for their proposals, reports and decisions.',
      ],
    },
    {
      titulo: 'Who is behind Sinapser',
      parrafos: [
        'Sinapser was founded and is run by Iván García Villar, co-founder of Eventscase, where he has led technology since 2016. The events platform grew from 0 to 80 people and raised €8 million before it was sold in 2024.',
        'His experience applying AI to events began in 2013 at Pickevent, the London startup that Eventscase grew out of. There, he was already using AI to make recommendations.',
      ],
    },
    {
      titulo: 'Other products we have built',
      parrafos: [
        'EVA looks after attendees on WhatsApp and web, with no app to download: tickets, agenda, alerts and answers during the event. With EVA Insights, organisers can review the questions the assistant receives.',
        'EVA is the first WhatsApp AI agent for events in Spain and has been running real events since 2024. It was named Innovation Champion at the MPI Iberian Awards 2025.',
        'EVA Brain helps events teams find information in their emails, meetings and documents, with references to the sources.',
        'We also design AI activations for events and offer AI training, consultancy and implementation.',
      ],
    },
    {
      titulo: 'Get in touch',
      parrafos: [
        `Know a study that should be in Athena? Write to ${CONTACTO_SINAPSER}. You can also contact us to talk about Athena or tell us about a project. More about Sinapser at sinapser.com.`,
      ],
    },
  ],
};

/**
 * Reescrito el 2026-09-27 (CUENTAS-01, decisión de Iván): Athena deja de ser "sin cuenta". Borrador
 * de Iván para que lo redacte quien lleva el bucle; Iván lo revisa antes de abrir el registro al
 * público (queda como v1 publicable en local, no una versión final sin más revisión).
 */
export const PRIVACIDAD: Readonly<Record<Idioma, readonly Seccion[]>> = {
  es: [
    {
      titulo: 'Qué se guarda',
      parrafos: [
        'La pregunta que escribes, la respuesta que se te devuelve y el camino que se siguió para construirla. Sirve para poder comprobar meses después que una cifra venía de donde decimos. Se guarda para análisis interno del funcionamiento de Athena y no se publica ni se indexa. Solo el equipo de Sinapser puede leerlas, para mejorar el servicio. Se conservan como máximo 24 meses y nunca se usan con fines publicitarios. Si preguntas con tu cuenta iniciada, la pregunta queda asociada a tu cuenta: el equipo de Sinapser puede ver con qué correo se hizo.',
        'Una cookie técnica de sesión, firmada, que no identifica a nadie por sí sola: sostiene la cuota y te deja volver a tu propia respuesta. Dura treinta días.',
        'Si te registras: tu correo, la fecha de alta y, de cada consentimiento que dieras (términos, newsletter), qué versión del texto aceptaste, el texto exacto que leíste, la fecha y si luego te has dado de baja.',
      ],
    },
    {
      titulo: 'Qué no se guarda',
      parrafos: [
        'Sin registrarte no hay correo ni contraseña: la cookie de sesión anónima basta para usar Athena. No hay cookies de terceros ni de rastreo. No se comparten datos con anunciantes, porque no hay anunciantes.',
        'Tus consultas no se publican. Solo las ve tu propia sesión (o tu cuenta, si has iniciado sesión), aunque compartas el enlace: a cualquier otra el servidor le responde que no existe.',
        'Si compartes fuera de Athena un fragmento literal de una respuesta, pedimos que enlaces a la fuente en la web de Athena, para que quien lo lea pueda comprobarlo.',
      ],
    },
    {
      titulo: 'Registro y newsletter',
      parrafos: [
        'El registro es por enlace mágico al correo, sin contraseña. Aceptar los términos es obligatorio para registrarte; suscribirte a la newsletter de nuevos estudios y novedades de la industria es una casilla aparte, sin marcar por defecto, y puede incluir información de productos de Sinapser. Te das de baja con un clic, desde el propio correo o desde tu cuenta.',
        'El nivel de la cuenta (normal o superior) decide solo cuántas preguntas puedes hacer; no cambia lo que Athena guarda de ti ni para qué lo usa.',
      ],
    },
    {
      titulo: 'Base legal y tus derechos',
      parrafos: [
        'La base legal es el interés legítimo de prestar el servicio y poder auditarlo, y el consentimiento para la newsletter. Se aplica el UK GDPR, y el RGPD para quien visite desde la Unión Europea.',
        `Puedes pedir que se borre lo asociado a tu cuenta o a tu sesión escribiendo a ${CONTACTO}. Sin cuenta, harán falta el identificador de la consulta o la fecha para encontrarlo.`,
      ],
    },
    {
      titulo: 'Última actualización',
      parrafos: [
        '1 de octubre de 2026: las preguntas que haces con tu cuenta iniciada quedan asociadas a tu cuenta, y el equipo de Sinapser puede ver con qué correo se hizo cada una.',
        '28 de septiembre de 2026: se aclara quién lee las preguntas guardadas (solo el equipo de Sinapser), con qué fin (mejorar el servicio), cuánto tiempo se conservan (24 meses como máximo) y que nunca se usan con fines publicitarios.',
      ],
    },
  ],
  en: [
    {
      titulo: 'What is kept',
      parrafos: [
        'The question you write, the answer returned to you and the path taken to build it. It lets us check months later that a figure came from where we say it did. It is kept for internal analysis of how Athena works, and is not published or indexed. Only the Sinapser team can read them, to improve the service. They are kept for at most 24 months and are never used for advertising purposes. If you ask while logged in, the question is linked to your account: the Sinapser team can see which email it came from.',
        'A signed technical session cookie that does not identify anyone by itself: it supports the quota and lets you return to your own answer. It lasts thirty days.',
        'If you register: your email, the date you signed up and, for each consent you gave (terms, newsletter), which version of the text you accepted, the exact text you read, the date, and whether you later unsubscribed.',
      ],
    },
    {
      titulo: 'What is not kept',
      parrafos: [
        'Without registering there is no email or password: the anonymous session cookie is enough to use Athena. There are no third-party or tracking cookies. No data is shared with advertisers, because there are no advertisers.',
        'Your queries are not published. Only your own session (or your account, if you are logged in) sees them, even if you share the link: to any other one the server answers that it does not exist.',
        'If you share a literal fragment of an answer outside Athena, we ask that you link to the source on the Athena website, so whoever reads it can check it.',
      ],
    },
    {
      titulo: 'Registration and newsletter',
      parrafos: [
        "Registration is by magic link to your email, with no password. Accepting the terms is required to register; subscribing to the newsletter about new studies and industry news is a separate checkbox, unticked by default, and may include information about Sinapser's products. You can unsubscribe with one click, from the email itself or from your account.",
        'The account level (normal or above) only decides how many questions you can ask; it does not change what Athena keeps about you or what it is used for.',
      ],
    },
    {
      titulo: 'Legal basis and your rights',
      parrafos: [
        'The legal basis is the legitimate interest of providing the service and being able to audit it, and consent for the newsletter. UK GDPR applies, and GDPR for visitors from the European Union.',
        `You can ask for anything tied to your account or your session to be deleted by writing to ${CONTACTO}. Without an account, the query identifier or the date will be needed to find it.`,
      ],
    },
    {
      titulo: 'Last updated',
      parrafos: [
        '1 October 2026: questions you ask while logged in are linked to your account, and the Sinapser team can see which email each one came from.',
        '28 September 2026: clarifies who reads stored questions (only the Sinapser team), for what purpose (to improve the service), how long they are kept (at most 24 months), and that they are never used for advertising purposes.',
      ],
    },
  ],
};

/**
 * Términos y condiciones (CUENTAS-01, borrador de Iván redactado el 2026-09-27; queda como v1
 * publicable en local, no una versión final sin más revisión). Cada versión de este texto lleva su
 * fecha (`VERSION_TERMINOS`, importada de `@athena/shared`: el API también la necesita, para
 * comparar la versión que aceptó cada cuenta con la vigente).
 *
 * ADMIN-02, 2026-09-28: cambia el párrafo "Qué guardamos de tus búsquedas" (quién las lee, para
 * qué y cuánto tiempo), así que sube de versión. Con ella sube también el aviso de reconsentimiento
 * para quien ya tenga cuenta (`AvisoReconsentimiento.tsx`).
 *
 * MCP-01, 2026-09-29: añade la sección "Uso por MCP (agentes de IA)" (cita siempre la fuente, no
 * redistribuir el corpus). Sube de versión otra vez, por el mismo mecanismo de reconsentimiento.
 *
 * ADMIN-03, 2026-10-01: las preguntas hechas con la cuenta iniciada quedan asociadas a la cuenta y
 * `/admin` enseña su correo. Sube de versión: el API solo asocia la pregunta a una cuenta que ya ha
 * aceptado esta versión (`marcarOrigen.ts`), así que nadie queda asociado sin haberlo leído.
 */
export const TERMINOS: Readonly<Record<Idioma, readonly Seccion[]>> = {
  es: [
    {
      titulo: 'Qué es Athena',
      parrafos: [
        'Athena es un producto de Sinapser: preguntas y comparaciones sobre la industria de eventos, respondidas desde un catálogo de estudios públicos, con cada cifra enlazada a su fuente.',
        'Athena no publica veredictos ni puntuaciones sobre las fuentes: enseña qué declara cada estudio y quién lo paga, y quien lee decide.',
      ],
    },
    {
      titulo: 'Qué guardamos de tus búsquedas',
      parrafos: [
        'Guardamos las preguntas y respuestas para análisis interno del funcionamiento de Athena. No se publican ni se indexan. Solo el equipo de Sinapser puede leerlas, para mejorar el servicio. Se conservan como máximo 24 meses y nunca se usan con fines publicitarios. Si preguntas con tu cuenta iniciada, la pregunta queda asociada a tu cuenta: el equipo de Sinapser puede ver con qué correo se hizo.',
        'Si compartes fuera de Athena un fragmento literal de una respuesta, pedimos que enlaces a la fuente correspondiente en la web de Athena.',
      ],
    },
    {
      titulo: 'Registro y cuotas',
      parrafos: [
        'El registro es por enlace mágico al correo, sin contraseña. Hay un límite de preguntas por día, distinto según tengas cuenta o no; Athena lo dice cuando se alcanza.',
        'El nivel superior no se vende: no hay ningún plan de pago en Athena.',
      ],
    },
    {
      titulo: 'Uso por MCP (agentes de IA)',
      parrafos: [
        'Si conectas tu cuenta al servidor MCP de Athena, el agente que uses (ChatGPT, Claude u otro) puede leer el catálogo de estudios en tu nombre, solo lectura. Cada dato que use tiene que citar su fuente y su página, igual que en la web.',
        'No se puede redistribuir el corpus (los textos, las cifras o las citas literales) fuera de una respuesta puntual a tu pregunta.',
      ],
    },
    {
      titulo: 'Aceptación',
      parrafos: [`Al registrarte aceptas estos términos. Puedes escribir a ${CONTACTO} con cualquier duda o para pedir que se corrija o borre algo.`],
    },
    {
      titulo: 'Última actualización',
      parrafos: [
        '1 de octubre de 2026: las preguntas que haces con tu cuenta iniciada quedan asociadas a tu cuenta, y el equipo de Sinapser puede ver con qué correo se hizo cada una.',
        '29 de septiembre de 2026: se añade la sección "Uso por MCP (agentes de IA)", para el servidor MCP de Athena.',
        '28 de septiembre de 2026: se aclara quién lee las preguntas guardadas (solo el equipo de Sinapser), con qué fin (mejorar el servicio), cuánto tiempo se conservan (24 meses como máximo) y que nunca se usan con fines publicitarios.',
      ],
    },
  ],
  en: [
    {
      titulo: 'What Athena is',
      parrafos: [
        'Athena is a Sinapser product: questions and comparisons about the events industry, answered from a catalogue of public studies, with every figure linked to its source.',
        'Athena does not publish verdicts or scores about its sources: it shows what each study states and who pays for it, and the reader decides.',
      ],
    },
    {
      titulo: 'What we keep from your searches',
      parrafos: [
        'We keep questions and answers for internal analysis of how Athena works. They are not published or indexed. Only the Sinapser team can read them, to improve the service. They are kept for at most 24 months and are never used for advertising purposes. If you ask while logged in, the question is linked to your account: the Sinapser team can see which email it came from.',
        'If you share a literal fragment of an answer outside Athena, we ask that you link to the corresponding source on the Athena website.',
      ],
    },
    {
      titulo: 'Registration and quotas',
      parrafos: [
        'Registration is by magic link to your email, with no password. There is a daily question limit, different depending on whether you have an account or not; Athena says so when it is reached.',
        'The higher level is not sold: there is no paid plan on Athena.',
      ],
    },
    {
      titulo: 'Use via MCP (AI agents)',
      parrafos: [
        "If you connect your account to Athena's MCP server, the agent you use (ChatGPT, Claude or another) can read the catalogue of studies on your behalf, read-only. Every figure it uses must cite its source and page, just as on the website.",
        'The corpus (texts, figures or literal quotes) cannot be redistributed outside a single answer to your question.',
      ],
    },
    {
      titulo: 'Acceptance',
      parrafos: [
        `By registering you accept these terms. You can write to ${CONTACTO} with any question or to ask for something to be corrected or deleted.`,
      ],
    },
    {
      titulo: 'Last updated',
      parrafos: [
        '1 October 2026: questions you ask while logged in are linked to your account, and the Sinapser team can see which email each one came from.',
        '29 September 2026: adds the "Use via MCP (AI agents)" section, for the Athena MCP server.',
        '28 September 2026: clarifies who reads stored questions (only the Sinapser team), for what purpose (to improve the service), how long they are kept (at most 24 months), and that they are never used for advertising purposes.',
      ],
    },
  ],
};

export const AVISO_LEGAL: Readonly<Record<Idioma, readonly Seccion[]>> = {
  es: [
    {
      titulo: 'Titular',
      parrafos: [`Iván García Villar, autónomo en Reino Unido, titular de la marca Sinapser AI, registrada en España. Contacto: ${CONTACTO}.`],
    },
    {
      titulo: 'Sobre las citas de los estudios',
      parrafos: [
        'Athena presenta citas breves de estudios de terceros al amparo del derecho de cita, con referencias a la publicación y a la página o ubicación del dato. Identifica la organización editora y los detalles disponibles del estudio, y enlaza a la publicación original para consultar sus resultados completos y su contexto.',
        `Si eres el editor de un estudio y quieres que se corrija algo o que se deje de citar, escribe a ${CONTACTO} y se hace.`,
      ],
    },
    {
      titulo: 'Sin garantías sobre los estudios',
      parrafos: [
        'Athena enseña lo que dicen los estudios, no afirma que sea cierto. Cada cifra va con quién la midió, a quién preguntó y quién lo paga, justamente para que puedas juzgarla tú.',
      ],
    },
  ],
  en: [
    {
      titulo: 'Owner',
      parrafos: [
        `Iván García Villar, sole trader in the United Kingdom, owner of the Sinapser AI trademark registered in Spain. Contact: ${CONTACTO}.`,
      ],
    },
    {
      titulo: 'About quoting the studies',
      parrafos: [
        'Athena presents brief quotations from third-party studies under the right of quotation, with references to the publication and the page or location of each finding. It identifies the publishing organisation and available study details, and links to the original publication for its full results and context.',
        `If you publish a study and want something corrected or no longer quoted, write to ${CONTACTO} and it will be done.`,
      ],
    },
    {
      titulo: 'No warranty about the studies',
      parrafos: [
        'Athena shows what the studies say; it does not claim they are true. Every figure comes with who measured it, who they asked and who pays for it, precisely so that you can judge it yourself.',
      ],
    },
  ],
};

/**
 * El texto exacto que se guarda como consentimiento de términos: la unión de los párrafos de la
 * versión vigente (`VERSION_TERMINOS`). Usada al registrarse (`IniciarSesionPage.tsx`) y al
 * reconsentir tras subir de versión (`AvisoReconsentimiento.tsx`): las dos veces se guarda
 * literalmente lo mismo que se leyó, no un resumen.
 */
export function textoDeTerminos(idioma: Idioma): string {
  return TERMINOS[idioma]
    .flatMap(s => s.parrafos)
    .join(' ')
    .trim();
}
